import useResizeObserver from '@react-hook/resize-observer';
import { NoData } from 'pages/Dashboard/components/NoData';
import { createRenderActiveShape } from 'pages/Dashboard/components/pie_chart_util';
import {
    CenteredChartContainer,
    PieChartLegendContainer,
    PieChartLegendItem,
    StyledPieChart,
} from 'pages/Dashboard/components/styled';
import { useMemo, useRef, useState } from 'react';
import { Cell, Pie, Tooltip } from 'recharts';
import styled from 'styled-components';
import { useThemeValues } from 'styles/theme';

export type LegendVariant = 'simple' | 'detailed' | 'custom';
export type LegendPosition = 'right' | 'bottom' | 'left' | 'auto';

const SizeContainerAuto = styled.div`
    container-type: size;
    container-name: pie-chart-container;
    height: 100%;
`;

const Container = styled.div<{
    $legendPosition: LegendPosition;
}>`
    display: grid;
    row-gap: 0.4rem;
    column-gap: 1.6rem;
    height: 100%;
    position: relative;

    ${({ $legendPosition }) => {
        switch ($legendPosition) {
            case 'auto':
            case 'right':
                return `
                    grid-template-areas: 'chart legend';
                    grid-template-columns: 1fr auto;
                `;
            case 'left':
                return `
                    grid-template-areas: 'legend chart';
                    grid-template-columns: auto 1fr;
                `;
            case 'bottom':
                return `
                    grid-template-areas: 'chart' 'legend';
                    grid-template-rows: 1fr auto;
                `;
        }
    }}

    @container pie-chart-container (max-aspect-ratio: 5/3) {
        ${({ $legendPosition }) =>
            $legendPosition === 'auto' &&
            `
            grid-template-areas: 'chart' 'legend';
            grid-template-columns: 1fr;
            grid-template-rows: 1fr auto;
        `}
    }
`;

const ChartContainerWithArea = styled(CenteredChartContainer)`
    grid-area: chart;
`;

const LegendContainerWithArea = styled(PieChartLegendContainer)<{
    $legendPosition: LegendPosition;
}>`
    grid-area: legend;

    ${({ $legendPosition }) => {
        switch ($legendPosition) {
            case 'right':
            case 'left':
            case 'auto':
                return `
            justify-content: center;
        `;
            case 'bottom':
                return `
            max-height: calc(3 * 1.4rem + 3 * 1rem + 1rem);
            flex-wrap: wrap;
            overflow-x: auto;
        `;
        }
    }}

    @container pie-chart-container (max-aspect-ratio: 5/3) {
        ${({ $legendPosition }) =>
            $legendPosition === 'auto' &&
            `
            justify-content: flex-start;
            max-height: calc(3 * 1.4rem + 3 * 1rem + 1rem);
            flex-wrap: wrap;
            overflow-x: auto;
        `}
    }

    /* for small containers, force the legend to take less space, regardless of
    aspect ratio, to ensure the pie is not completely overlapping itself */
    @container pie-chart-container  (max-height: 40rem) {
        max-height: calc(2 * 1.4rem + 2 * 1rem + 1rem);
    }
`;

type PieChartWithLegendProps<T extends PieChartDatum> = {
    /** Data for the pie chart - must include fill property */
    data: T[];
    /**
     * Position of the legend relative to the chart. Use 'auto' to
     * automatically position right (landscape) or bottom (portrait) based on
     * container aspect ratio.
     * */
    legendPosition?: LegendPosition;
    /** Legend variant: 'simple' (just labels), 'detailed' (with values/percentages), 'custom' (provide renderLegendItem) */
    legendVariant?: LegendVariant;
    /** Inner radius of the donut (0-100, where 0 is a full pie) */
    innerRadius?: number | string;
    /** Outer radius of the donut */
    outerRadius?: number | string;
    /** Custom primary text formatter for active shape */
    formatPrimaryText?: (item: T) => string;
    /** Custom secondary text formatter for active shape */
    formatSecondaryText?: (item: T) => string;
    /** Legend color indicator shape */
    legendIconVariant?: 'circle' | 'square';
    /** Gap between legend items */
    legendGap?: string;
    /** Custom container className */
    className?: string;
};

export type PieChartDatum = {
    name: string;
    value: number;
    fill: string;
};

function ResponsivePieChart<T extends PieChartDatum>({
    data,
    innerRadius,
    outerRadius,
    formatPrimaryText,
    formatSecondaryText,
}: {
    data: T[];
    innerRadius: number | string;
    outerRadius: number | string;
    formatPrimaryText: (item: T) => string;
    formatSecondaryText?: (item: T) => string;
}) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [[width, height], setDimensions] = useState<
        [number, number] | [undefined, undefined]
    >([undefined, undefined]);
    const theme = useThemeValues();

    const renderActiveShape = useMemo(
        () =>
            createRenderActiveShape(
                theme.primaryText,
                theme.secondaryText,
                (params) => formatPrimaryText((params as unknown) as T),
                formatSecondaryText
                    ? (params) => formatSecondaryText((params as unknown) as T)
                    : undefined,
                15
            ),
        // rebuilding fn on width/height change to make the drawn object responsive
        // internal text scaling gets weird otherwise
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            theme.primaryText,
            theme.secondaryText,
            width,
            height,
            formatPrimaryText,
            formatSecondaryText,
        ]
    );

    useResizeObserver(wrapperRef, (e) => {
        setDimensions([e.contentRect.width, e.contentRect.height]);
    });

    return (
        <div
            ref={wrapperRef}
            style={{
                // wrapper with containerType is to deal with trouble getting the parent
                // to enforce sizing on it, which previously led to overflow
                justifySelf: 'stretch',
                alignSelf: 'stretch',
                containerType: 'size',
                flexGrow: 1,
            }}
        >
            <StyledPieChart
                style={{
                    aspectRatio: 1,
                    width: 'min(100cqw, 100cqh)',
                    margin: 'auto',
                }}
                responsive
            >
                <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={outerRadius}
                    innerRadius={innerRadius}
                    labelLine={false}
                    activeShape={renderActiveShape}
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                </Pie>

                {/* makes the last hovered thing sticky, and starts 'hovering'
                    the first piece of data, so less dead space */}
                <Tooltip content={() => null} defaultIndex={0} active={true} />

                {/* force "active" element on top of others */}
                {/* <use xlinkHref="#active" /> */}
            </StyledPieChart>
        </div>
    );
}

export function PieChartWithLegend<T extends PieChartDatum>({
    data,
    legendPosition = 'right',
    legendVariant = 'simple',
    innerRadius = '65%',
    outerRadius = '88%',
    formatPrimaryText,
    formatSecondaryText,
    legendIconVariant = 'circle',
    legendGap = '1rem',
    className,
}: PieChartWithLegendProps<T>) {
    const theme = useThemeValues();

    const totalValue = useMemo(() => data.reduce((sum, item) => sum + item.value, 0), [
        data,
    ]);

    const defaultFormatPrimaryText = useMemo(
        () => formatPrimaryText || ((item: T) => item.name),
        [formatPrimaryText]
    );

    const defaultSecondaryText = useMemo(
        () =>
            formatSecondaryText ||
            ((item: T) => {
                const value = item.value;
                const percent = totalValue > 0 ? (value / totalValue) * 100 : 0;
                return `${value.toLocaleString()} (${percent.toFixed(2)}%)`;
            }),
        [formatSecondaryText, totalValue]
    );

    const renderLegend = () => {
        if (legendVariant === 'detailed') {
            return data.map((item) => {
                const value = item.value;
                const name = item.name;
                return (
                    <PieChartLegendItem
                        key={name}
                        $color={item.fill}
                        $variant={legendIconVariant}
                        $gap="0.8ch"
                    >
                        <div>
                            <div style={{ fontWeight: 600, color: theme.primaryText }}>
                                {name}
                            </div>
                            <div>
                                {value.toLocaleString()} (
                                {totalValue > 0
                                    ? ((value / totalValue) * 100).toFixed(1)
                                    : 0}
                                %)
                            </div>
                        </div>
                    </PieChartLegendItem>
                );
            });
        }

        // Simple variant
        return data
            .filter((item) => item.value > 0)
            .map((item) => {
                const name = item.name;
                return (
                    <PieChartLegendItem
                        key={name}
                        $color={item.fill}
                        $variant={legendIconVariant}
                        $gap="0.5ch"
                    >
                        {name}
                    </PieChartLegendItem>
                );
            });
    };

    const content = (
        <Container $legendPosition={legendPosition} className={className}>
            {(data.length === 0 || data.every((item) => item.value === 0)) && <NoData />}

            <ChartContainerWithArea>
                <ResponsivePieChart<T>
                    data={data}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    formatPrimaryText={defaultFormatPrimaryText}
                    formatSecondaryText={defaultSecondaryText}
                />
            </ChartContainerWithArea>

            <LegendContainerWithArea $legendPosition={legendPosition} $gap={legendGap}>
                {renderLegend()}
            </LegendContainerWithArea>
        </Container>
    );

    // For 'auto' positioning, wrap in a size container so container queries can work
    if (legendPosition === 'auto') {
        return <SizeContainerAuto>{content}</SizeContainerAuto>;
    }

    return content;
}
